class ServiceFeeHandler {
  constructor() {
    // Set a debug flag if needed (set window.DEBUG = true for verbose logging)
    this.DEBUG = window.DEBUG || false;
    this.log('Initializing ServiceFeeHandler...');

    this.SERVICE_FEE_VARIANT_ID = '49082495009033';
    this.SHIPPING_DIGITAL_AND_POST = 'Digital & Postversand';
    this.SHIPPING_DIGITAL_ONLY = 'Nur Digital';

    // State management
    this.isInitialized = false;
    this.updateQueue = Promise.resolve();
    this.queueTimeout = null;
    this.kaktusObserver = null;

    // No debounce for immediate update (for testing)
    this.handleCartUpdate = this.handleCartUpdate.bind(this);

    // Initialize when DOM is ready
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.initialize());
    } else {
      this.initialize();
    }
  }

  log(...args) {
    if (this.DEBUG) console.log('ServiceFeeHandler:', ...args);
  }

  error(...args) {
    if (this.DEBUG) console.error('ServiceFeeHandler:', ...args);
  }

  initialize() {
    if (this.isInitialized) return;
    this.log('Starting core functionality');

    // Attach event listeners for cart updates:
    document.addEventListener('cart:add-complete', this.handleCartUpdate);
    document.addEventListener('cart:change-complete', this.handleCartUpdate);
    document.addEventListener('cart:remove-complete', () => {
      this.handleCartUpdate();
      this.refreshCartDrawer();
    });

    // Kaktus-specific integration
    this.setupKaktusObservers();

    // Initial update
    this.handleCartUpdate();
    this.isInitialized = true;
  }

  async handleCartUpdate() {
    return this.addToQueue(async () => {
      try {
        const cart = await this.getCartState();
        if (!cart) return;

        // Identify items that require postal shipping
        const postalItems = cart.items.filter(item =>
          item.variant_id.toString() !== this.SERVICE_FEE_VARIANT_ID &&
          (item.properties?.Lieferoption === this.SHIPPING_DIGITAL_AND_POST ||
           item.properties?.Versand === this.SHIPPING_DIGITAL_AND_POST)
        );

        this.log('Processing', postalItems.length, 'postal items');

        if (postalItems.length > 0) {
          const pageCount = this.calculateTotalPages(cart);
          this.log('Total pages calculated:', pageCount);
          await this.updateServiceFee(pageCount, postalItems[0]);
        } else {
          await this.removeServiceFee();
        }

        // Refresh the cart drawer after updating the fee
        await this.refreshCartDrawer();
      } catch (error) {
        this.error('Update cycle failed', error);
      }
    });
  }

  addToQueue(task) {
    return new Promise((resolve) => {
      this.updateQueue = this.updateQueue
        .then(task)
        .then(resolve)
        .catch((error) => {
          this.error('Queue error', error);
          resolve();
        });

      clearTimeout(this.queueTimeout);
      this.queueTimeout = setTimeout(() => {
        this.log('Queue reset after timeout');
        this.updateQueue = Promise.resolve();
        this.handleCartUpdate();
      }, 10000);
    });
  }

  calculateTotalPages(cart) {
    const total = cart.items.reduce((acc, item) => {
      // Skip the service fee item
      if (item.variant_id.toString() === this.SERVICE_FEE_VARIANT_ID) return acc;
      
      const hasPostShipping = [this.SHIPPING_DIGITAL_AND_POST].includes(
        item.properties?.Lieferoption || item.properties?.Versand
      );

      if (hasPostShipping) {
        const uploadCount = Object.keys(item.properties || {}).filter(k =>
          k.startsWith('Upload_') && item.properties[k]
        ).length;
        this.log(`Item ${item.id} has ${uploadCount} uploads; quantity: ${item.quantity}`);
        acc += Math.ceil(item.quantity || 1);
      }
      return acc;
    }, 0);

    this.log('Total pages (service fee quantity):', total);
    return total;
  }

  async getCartState() {
    try {
      // Append cache-busting timestamp
      const response = await fetch(`/cart.js?t=${new Date().getTime()}`);
      return await response.json();
    } catch (error) {
      this.error('Cart fetch failed', error);
      return null;
    }
  }

  async updateServiceFee(pageCount, mainItem) {
    try {
      // Remove any existing service fee items first
      await this.removeServiceFee();

      if (pageCount > 0) {
        const response = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            items: [{
              id: this.SERVICE_FEE_VARIANT_ID,
              quantity: pageCount,
              properties: {
                _Lieferoption: mainItem.properties?.Lieferoption || mainItem.properties?.Versand,
                _Versand: mainItem.properties?.Lieferoption || mainItem.properties?.Versand,
                _type: 'service_fee',
                _hidden: 'true',
                _locked: 'true'
              }
            }]
          })
        });
        if (!response.ok) throw await response.json();

        // Update locked items attribute (if needed)
        const cart = await this.getCartState();
        const lockedItems = cart.items
          .filter(item => item.properties?._locked)
          .map(item => item.key)
          .join(',');

        await fetch('/cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            attributes: { 'Locked Items': lockedItems }
          })
        });

        this.log(`Added service fee with quantity ${pageCount}`);
      }
    } catch (error) {
      this.error('Fee update failed', error);
    }
  }

  async removeServiceFee() {
    try {
      const cart = await this.getCartState();
      const updates = cart.items.reduce((acc, item) => {
        if (item.variant_id.toString() === this.SERVICE_FEE_VARIANT_ID) {
          acc[item.key] = 0;
        }
        return acc;
      }, {});

      if (Object.keys(updates).length > 0) {
        await fetch('/cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ 
            updates,
            attributes: { 'Locked Items': '' }
          })
        });
        this.log('Removed existing service fee items');
      }
    } catch (error) {
      this.error('Fee removal error', error);
    }
  }

  async refreshCartDrawer() {
    // This function fetches the latest cart state and calls updateCartDrawer.
    try {
      const response = await fetch(`/cart.js?t=${new Date().getTime()}`);
      const cart = await response.json();
      this.log('Refreshing cart drawer with cart:', cart);
      if (typeof window.updateCartDrawer === 'function') {
        window.updateCartDrawer(cart);
      } else {
        this.log('updateCartDrawer() function not defined.');
      }
    } catch (error) {
      this.error('Cart drawer refresh failed', error);
    }
  }

  setupKaktusObservers() {
    const setupObservers = () => {
      const kaktusContainer = document.querySelector('.kaktusc-cart__products');
      if (!kaktusContainer) return;

      // Prevent manual deletion of service fee from Kaktus UI
      kaktusContainer.addEventListener('click', (e) => {
        const deleteBtn = e.target.closest('.kaktusc-cart__product-delete');
        if (deleteBtn && deleteBtn.closest(`[data-id="${this.SERVICE_FEE_VARIANT_ID}"]`)) {
          e.preventDefault();
          e.stopImmediatePropagation();
          this.log('Prevented manual deletion of service fee');
          this.handleCartUpdate();
        }
      });

      // Observe mutations in the Kaktus container
      this.kaktusObserver = new MutationObserver(() => {
        this.log('Kaktus cart mutation detected');
        this.handleCartUpdate();
      });
      this.kaktusObserver.observe(kaktusContainer, {
        childList: true,
        subtree: true
      });
    };

    const poll = setInterval(() => {
      if (document.querySelector('.kaktusc-cart__products')) {
        clearInterval(poll);
        setupObservers();
      }
    }, 500);
  }
}

// Use a singleton instance
if (!window.serviceFeeHandler) {
  window.serviceFeeHandler = new ServiceFeeHandler();
} else {
  console.warn('ServiceFeeHandler: Already initialized');
}

/* =========================
   Custom Cart Drawer Update
   ========================= */
// This function updates the Kaktus cart drawer UI using the latest cart JSON.
// Adjust the HTML structure as needed.
function updateCartDrawer(cart) {
  var $container = $('.kaktusc-cart__products');
  if (!$container.length) return;

  $container.empty();
  if (cart.items && cart.items.length > 0) {
    cart.items.forEach(function(item, index) {
      // Build HTML based on the structure you provided.
      var html = '<div class="kaktusc-cart__product" data-id="' + item.variant_id + '" data-line="' + (index + 1) + '">';
      // Product image
      if (item.featured_image && item.featured_image.url) {
        html += '<div class="kaktusc-cart__product-img">';
        html += '<img src="' + item.featured_image.url + '" alt="Product">';
        html += '</div>';
      }
      // Content: title, variant, quantity controls (if applicable)
      html += '<div class="kaktusc-cart__product-content">';
      html += '<h4 class="kaktusc-cart__product-title kaktusc-prod--title">' + item.title + '</h4>';
      if (item.variant_options && item.variant_options.length > 0) {
        html += '<span class="kaktusc-cart__product-variant">' + item.variant_options.join(' / ') + '</span>';
      }
      html += '<div class="kaktusc-cart__product-qnt-wrap">';
      // Minus button (for non-locked items; assume service fee is locked)
      if (item.variant_id.toString() !== "49082495009033") {
        html += '<span class="kaktusc-cart__product-qnt--minus cart-prod-qnt">';
        html += '<svg width="15" height="2" viewBox="0 0 15 2" fill="none" xmlns="http://www.w3.org/2000/svg"><path class="cart-prod-qnt-s" d="M14.0625 0H0.9375C0.68886 0 0.450403 0.105357 0.274588 0.292893C0.0987722 0.48043 0 0.734784 0 1C0 1.26522 0.0987722 1.51957 0.274588 1.70711C0.450403 1.89464 0.68886 2 0.9375 2H14.0625C14.3111 2 14.5496 1.89464 14.7254 1.70711C14.9012 1.51957 15 1.26522 15 1C15 0.734784 14.9012 0.48043 14.7254 0.292893C14.5496 0.105357 14.3111 0 14.0625 0Z" fill="#999999"></path></svg>';
        html += '</span>';
      }
      html += '<span class="kaktusc-cart__product-qnt" data-qnt="' + item.quantity + '">' + item.quantity + '</span>';
      // Plus button (only for non-locked)
      if (item.variant_id.toString() !== "49082495009033") {
        html += '<span class="kaktusc-cart__product-qnt--plus cart-prod-qnt plus">';
        html += '<svg width="15" height="15" viewBox="0 0 15 15" fill="none" xmlns="http://www.w3.org/2000/svg"><path class="cart-prod-qnt-s" d="M14.0625 6.5625H8.4375V0.9375C8.4375 0.68886 8.33873 0.450403 8.16291 0.274588C7.9871 0.0987722 7.74864 0 7.5 0C7.25136 0 7.0129 0.0987722 6.83709 0.274588C6.66127 0.450403 6.5625 0.68886 6.5625 0.9375V6.5625H0.9375C0.68886 6.5625 0.450403 6.66127 0.274588 6.83709C0.0987722 7.0129 0 7.25136 0 7.5C0 7.74864 0.0987722 7.9871 0.274588 8.16291C0.450403 8.33873 0.68886 8.4375 0.9375 8.4375H6.5625V14.0625C6.5625 14.3111 6.66127 14.5496 6.83709 14.7254C7.0129 14.9012 7.25136 15 7.5 15C7.74864 15 7.9871 14.9012 8.16291 14.7254C8.33873 14.5496 8.4375 14.3111 8.4375 14.0625V8.4375H14.0625C14.3111 8.4375 14.5496 8.33873 14.7254 8.16291C14.9012 7.9871 15 7.74864 15 7.5C15 7.25136 14.9012 7.0129 14.7254 6.83709C14.5496 6.66127 14.3111 6.5625 14.0625 6.5625Z" fill="#999999"></path></svg>';
        html += '</span>';
      }
      html += '<div class="kaktusc-cart__product-err hide"></div>';
      html += '</div>'; // end quantity wrap
      html += '</div>'; // end product content

      // Price section and delete button (only for non-locked items)
      html += '<div class="kaktusc-cart__product-price-section">';
      if (item.variant_id.toString() !== "49082495009033") {
        html += '<div class="kaktusc-cart__product-delete">';
        html += '<svg width="18" height="20" viewBox="0 0 18 20" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M1.5 4.99999H3.16667M3.16667 4.99999H16.5M3.16667 4.99999V16.6667C3.16667 17.1087 3.34226 17.5326 3.65482 17.8452C3.96738 18.1577 4.39131 18.3333 4.83333 18.3333H13.1667C13.6087 18.3333 14.0326 18.1577 14.3452 17.8452C14.6577 17.5326 14.8333 17.1087 14.8333 16.6667V4.99999H3.16667ZM5.66667 4.99999V3.33332C5.66667 2.8913 5.84226 2.46737 6.15482 2.15481C6.46738 1.84225 6.89131 1.66666 7.33333 1.66666H10.6667C11.1087 1.66666 11.5326 1.84225 11.8452 2.15481C12.1577 2.46737 12.3333 2.8913 12.3333 3.33332V4.99999" stroke="#999999" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"></path></svg>';
        html += '</div>';
      }
      html += '<div class="kaktusc-cart__product-price-wrap">';
      // Use discounted price if available, else real price.
      html += '<span class="kaktusc-cart__product-price-discounted">' + (item.final_line_price ? formatMoney(item.final_line_price) : '') + '</span>';
      html += '</div>';
      html += '</div>'; // end price section

      html += '</div>'; // end product container

      $container.append(html);
    });
  } else {
    $container.html('<p>Your shopping cart is empty</p>');
  }
}

// Simple helper to format money (assumes euros, adjust if needed)
function formatMoney(cents) {
  // Convert cents to euros and add € sign. Adjust decimal places as needed.
  return (cents / 100).toFixed(2) + ' €';
}
